import { useCallback, useEffect, useState } from "react";
import {
  listClaimRequests,
  approveClaimRequest,
  rejectClaimRequest,
} from "../../../api/playerVault";
import type { ClaimRequest, ClaimRequestListParams, ClaimRequestStatus } from "../../../api/playerVault";

const DEFAULT_PAGE_SIZE = 20;

export function useClaimRequests() {
  const [params, setParamsState] = useState<ClaimRequestListParams>({
    pageNum: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [requests, setRequests] = useState<ClaimRequest[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchList = useCallback(async (overrideParams?: ClaimRequestListParams) => {
    setListLoading(true);
    setError(null);
    try {
      const merged = overrideParams ?? params;
      const response = await listClaimRequests(merged);
      setRequests(response.results);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      setError(err?.message ?? "Failed to load claim requests.");
    } finally {
      setListLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const setPageNum = useCallback((pageNum: number) => {
    setParamsState((prev) => ({ ...prev, pageNum }));
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setParamsState((prev) => ({ ...prev, pageSize, pageNum: 1 }));
  }, []);

  const setStatus = useCallback((status?: ClaimRequestStatus) => {
    setParamsState((prev) => ({ ...prev, status, pageNum: 1 }));
  }, []);

  const approve = useCallback(
    async (id: string): Promise<void> => {
      setActionLoading(true);
      try {
        await approveClaimRequest(id);
        await fetchList();
      } finally {
        setActionLoading(false);
      }
    },
    [fetchList]
  );

  const reject = useCallback(
    async (id: string, reason: string): Promise<void> => {
      setActionLoading(true);
      try {
        await rejectClaimRequest(id, reason);
        await fetchList();
      } finally {
        setActionLoading(false);
      }
    },
    [fetchList]
  );

  return {
    functions: {
      fetchList,
      setPageNum,
      setPageSize,
      setStatus,
      approve,
      reject,
    },
    values: {
      requests,
      params,
      totalCount,
      totalPages,
      error,
    },
    loading: {
      listLoading,
      actionLoading,
    },
  };
}
